export type RelativeColorStrategy = "age" | "deadline" | "customer-activity";

export type RelativeMode = true | RelativeColorStrategy;

type BadgeColor = "green" | "gold" | "red" | "muted";

export const badgeColorStyles: Record<BadgeColor, string> = {
  green: "bg-green/10 text-success-strong border-green/20",
  gold: "bg-very-light-orange text-warning-strong border-orange/20",
  red: "bg-very-light-red text-danger-strong border-red/20",
  muted:
    "bg-rose-50 text-rose-500 border-rose-200 dark:bg-rose-950/60 dark:text-rose-400 dark:border-rose-700/50",
};

/**
 * Format a millisecond delta into a short relative label.
 * Positive ms = past, negative ms = future.
 *
 * Short: "4d ago", "2h ago", "in 3d"
 * With `noSuffix`: "4d", "2h", "3d" — useful when the surrounding context
 * (column header like "Open For") already implies the direction.
 */
export function formatRelativeLabelShort(ms: number, noSuffix = false): string {
  const isFuture = ms < 0;
  const absMs = Math.abs(ms);
  const totalMinutes = Math.floor(absMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  let label: string;
  if (totalMinutes < 1) {
    return "now";
  } else if (totalHours < 1) {
    label = `${totalMinutes}m`;
  } else if (totalHours < 48) {
    label = `${totalHours}h`;
  } else if (days < 30) {
    label = `${days}d`;
  } else if (days < 365) {
    label = `${Math.floor(days / 30)}mo`;
  } else {
    label = `${Math.floor(days / 365)}y`;
  }

  if (noSuffix) return label;
  return isFuture ? `in ${label}` : `${label} ago`;
}

/**
 * Format a millisecond delta into a long relative label.
 * Positive ms = past, negative ms = future.
 *
 * Long: "4 days ago", "2 hours ago", "in 3 days"
 */
export function formatRelativeLabelLong(ms: number): string {
  const isFuture = ms < 0;
  const absMs = Math.abs(ms);
  const totalMinutes = Math.floor(absMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  let label: string;
  if (totalMinutes < 1) {
    return "just now";
  } else if (totalHours < 1) {
    label = totalMinutes === 1 ? "1 minute" : `${totalMinutes} minutes`;
  } else if (totalHours < 48) {
    label = totalHours === 1 ? "1 hour" : `${totalHours} hours`;
  } else if (days < 30) {
    label = `${days} days`;
  } else if (days < 365) {
    const months = Math.floor(days / 30);
    label = months === 1 ? "1 month" : `${months} months`;
  } else {
    const years = Math.floor(days / 365);
    label = years === 1 ? "1 year" : `${years} years`;
  }

  return isFuture ? `in ${label}` : `${label} ago`;
}

/**
 * Format a day delta into a short relative label.
 * Positive days = past, negative days = future.
 *
 * Short: "4d ago", "in 3d"
 */
export function formatRelativeDaysShort(days: number): string {
  const isFuture = days < 0;
  const absDays = Math.abs(days);

  let label: string;
  if (absDays === 0) return "today";
  if (days === 1) return "1d ago";
  if (days === -1) return "in 1d";
  if (absDays < 30) {
    label = `${absDays}d`;
  } else if (absDays < 365) {
    label = `${Math.floor(absDays / 30)}mo`;
  } else {
    label = `${Math.floor(absDays / 365)}y`;
  }

  return isFuture ? `in ${label}` : `${label} ago`;
}

/**
 * Format a day delta into a long relative label.
 * Positive days = past, negative days = future.
 *
 * Long: "4 days ago", "yesterday", "tomorrow"
 */
export function formatRelativeDaysLong(days: number): string {
  const isFuture = days < 0;
  const absDays = Math.abs(days);

  let label: string;
  if (absDays === 0) return "today";
  if (days === 1) return "yesterday";
  if (days === -1) return "tomorrow";
  if (absDays < 30) {
    label = `${absDays} days`;
  } else if (absDays < 365) {
    const months = Math.floor(absDays / 30);
    label = months === 1 ? "1 month" : `${months} months`;
  } else {
    const years = Math.floor(absDays / 365);
    label = years === 1 ? "1 year" : `${years} years`;
  }

  return isFuture ? `in ${label}` : `${label} ago`;
}

/**
 * Format precise duration for hover tooltips.
 * Positive ms = past, negative ms = future.
 */
export function formatPreciseDuration(ms: number): string {
  const absMs = Math.abs(ms);
  const totalMinutes = Math.floor(absMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0 || parts.length === 0)
    parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  const joined = parts.join(", ");
  return ms < 0 ? `in ${joined}` : `${joined} ago`;
}

/**
 * Determine badge color based on millisecond delta and strategy.
 *
 * - `undefined` (relative=true): blue — informational, no urgency opinion
 * - `"age"`: green (recent) → gold (hours) → red (days+). For task durations, "open for" displays.
 * - `"deadline"`: green (far away) → gold (approaching) → red (overdue). For pickup/delivery deadlines.
 * - `"customer-activity"`: green (≤14d) → gold (≤30d) → red (>30d). For "last quoted / last shipped" recency
 *   on customer or carrier-agreement records — flags accounts going dormant.
 */
export function getRelativeColor(
  ms: number,
  strategy: RelativeColorStrategy | undefined,
): BadgeColor {
  if (!strategy) return "muted";

  const hours = Math.abs(ms) / 3600000;

  if (strategy === "age") {
    if (hours < 4) return "green";
    if (hours < 24) return "gold";
    return "red";
  }

  if (strategy === "customer-activity") {
    const days = Math.abs(ms) / 86_400_000;
    if (days <= 14) return "green";
    if (days <= 30) return "gold";
    return "red";
  }

  // deadline: past = overdue (red), approaching = gold, on track = green
  if (ms > 0) return "red"; // overdue
  if (hours < 24) return "gold"; // due within 24h
  return "green"; // on track
}

/**
 * Same logic but for day-granularity (Date component).
 */
export function getRelativeColorFromDays(
  days: number,
  strategy: RelativeColorStrategy | undefined,
): BadgeColor {
  if (!strategy) return "muted";

  const absDays = Math.abs(days);

  if (strategy === "age") {
    if (absDays === 0) return "green";
    if (absDays <= 3) return "gold";
    return "red";
  }

  if (strategy === "customer-activity") {
    if (absDays <= 14) return "green";
    if (absDays <= 30) return "gold";
    return "red";
  }

  // deadline: past = overdue (red), approaching = gold, on track = green
  if (days > 0) return "red"; // overdue
  if (absDays <= 1) return "gold"; // due tomorrow or today
  return "green"; // on track
}
